/**
 * Phase 27.50.B.5 — Cart count hook.
 *
 * Returns the current unit count in the authenticated user's cart and
 * re-fetches whenever:
 *   1. Route changes (so it stays in sync after navigation).
 *   2. Any component dispatches `window.dispatchEvent(new Event('cart:updated'))`.
 *
 * Fails soft — if the user is unauthenticated or the request errors, the
 * badge simply shows 0.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { shopAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function useCartCount() {
  const { user } = useAuth();
  const location = useLocation();
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!user) { setCount(0); return; }
    try {
      const r = await shopAPI.getCart();
      const total = (r.data?.items || []).reduce(
        (s, i) => s + (Number(i.quantity) || 0),
        0,
      );
      setCount(total);
    } catch {
      setCount(0);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh, location.pathname]);

  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('cart:updated', handler);
    return () => window.removeEventListener('cart:updated', handler);
  }, [refresh]);

  return count;
}

/** Notify all cart-aware components that the cart changed. */
export const notifyCartUpdated = () => {
  try { window.dispatchEvent(new Event('cart:updated')); } catch { /* ignore */ }
};
